/**
 * DsiaAnalyticsPanel — v49.7. Admin DS-IA / MyIA / Nano Banana usage analytics.
 * Displayed in /admin/analytics. Restricted to super_admin + managers server-side.
 * i18n: 4 admin languages (fr, en, es, zh).
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Sparkles, Loader2 } from 'lucide-react';
import { useAdminLang } from '../hooks/useAdminLang';

const L = {
  fr: {
    title: 'Destine IA — Analytiques', sub: 'Utilisation de MyIA, Avatar IA et Nano Banana',
    monthly: 'Vue mensuelle (12 mois)', yearly: 'Vue annuelle', total: 'Total', d30: '30 j', d365: '12 mois',
    error: 'Erreur', restricted: 'Réservé au super admin + managers',
    labels: {
      chat_text: 'MyIA — Messages texte', chat_voice: 'MyIA — Messages vocaux',
      poster_scan: "MyIA — Scan d'affiche", guess_movie: 'MyIA — Devine le film',
      culture_quiz: 'MyIA — Test culture ciné', translate_gim: 'MyIA — Traduction Gim Toli',
      translate_podcast: 'MyIA — Traduction podcast', image_generation: 'Nano Banana — Images',
      avatar_ia: 'Avatar IA — Générés',
    },
  },
  en: {
    title: 'Destine AI — Analytics', sub: 'Usage of MyAI, Avatar AI and Nano Banana',
    monthly: 'Monthly view (12 months)', yearly: 'Yearly view', total: 'Total', d30: '30 d', d365: '12 mo',
    error: 'Error', restricted: 'Super admin + managers only',
    labels: {
      chat_text: 'MyAI — Text messages', chat_voice: 'MyAI — Voice messages',
      poster_scan: 'MyAI — Poster scan', guess_movie: 'MyAI — Guess the movie',
      culture_quiz: 'MyAI — Cinema culture test', translate_gim: 'MyAI — Gim Toli translation',
      translate_podcast: 'MyAI — Podcast translation', image_generation: 'Nano Banana — Images',
      avatar_ia: 'AI Avatar — Generated',
    },
  },
  es: {
    title: 'Destine IA — Analíticas', sub: 'Uso de MyIA, Avatar IA y Nano Banana',
    monthly: 'Vista mensual (12 meses)', yearly: 'Vista anual', total: 'Total', d30: '30 d', d365: '12 mo',
    error: 'Error', restricted: 'Solo super admin + managers',
    labels: {
      chat_text: 'MyIA — Mensajes de texto', chat_voice: 'MyIA — Mensajes de voz',
      poster_scan: 'MyIA — Escanear cartel', guess_movie: 'MyIA — Adivina la película',
      culture_quiz: 'MyIA — Test cultura cine', translate_gim: 'MyIA — Traducir Gim Toli',
      translate_podcast: 'MyIA — Traducir podcast', image_generation: 'Nano Banana — Imágenes',
      avatar_ia: 'Avatar IA — Generados',
    },
  },
  zh: {
    title: 'Destine AI — 分析', sub: 'MyIA、AI 头像与 Nano Banana 使用情况',
    monthly: '按月视图（12 个月）', yearly: '按年视图', total: '合计', d30: '30 天', d365: '12 个月',
    error: '错误', restricted: '仅超级管理员和主管可见',
    labels: {
      chat_text: 'MyAI — 文字消息', chat_voice: 'MyAI — 语音消息',
      poster_scan: 'MyAI — 扫描海报', guess_movie: 'MyAI — 猜电影',
      culture_quiz: 'MyAI — 影史文化测试', translate_gim: 'MyAI — Gim Toli 翻译',
      translate_podcast: 'MyAI — 播客翻译', image_generation: 'Nano Banana — 图像生成',
      avatar_ia: 'AI 头像 — 已生成',
    },
  },
};

const COLORS = {
  chat_text: 'bg-cyan-500', chat_voice: 'bg-blue-500', poster_scan: 'bg-emerald-500',
  guess_movie: 'bg-amber-500', culture_quiz: 'bg-violet-500', translate_gim: 'bg-fuchsia-500',
  translate_podcast: 'bg-pink-500', image_generation: 'bg-rose-500', avatar_ia: 'bg-orange-500',
};

const DsiaAnalyticsPanel = () => {
  const { adminLang } = useAdminLang();
  const l = L[adminLang] || L.fr;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [forbidden, setForbidden] = useState(false);

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true); setForbidden(false);
      try {
        const r = await axios.get('/api/admin/dsia-analytics/summary', { params: { months: 12, years: 3 } });
        if (alive) setData(r.data);
      } catch (e) {
        if (e?.response?.status === 403) setForbidden(true);
        else toast.error(e?.response?.data?.detail || l.error);
      } finally { if (alive) setLoading(false); }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line

  if (forbidden) {
    return (
      <section className="p-4 rounded-3xl bg-white/5 border border-white/10" data-testid="dsia-panel-forbidden">
        <p className="text-sm text-white/60">🔒 {l.restricted}</p>
      </section>
    );
  }

  const maxMonth = data?.by_month?.reduce((m, r) => Math.max(m, r.total || 0), 1) || 1;

  return (
    <section className="p-4 rounded-3xl bg-gradient-to-br from-violet-500/10 to-fuchsia-500/10 border border-white/10 space-y-4" data-testid="dsia-analytics-panel">
      <div>
        <h3 className="text-lg font-bold flex items-center gap-2"><Sparkles className="w-5 h-5 text-fuchsia-300" /> {l.title}</h3>
        <p className="text-xs text-white/60">{l.sub}</p>
      </div>

      {loading || !data ? (
        <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-fuchsia-300" /></div>
      ) : (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {(data.features || []).map(f => (
              <div key={f.feature} className="p-3 rounded-xl bg-white/5 border border-white/10" data-testid={`dsia-feat-${f.feature}`}>
                <div className="flex items-center gap-2">
                  <span className={`w-2 h-2 rounded-full ${COLORS[f.feature] || 'bg-white/40'}`} />
                  <p className="text-xs text-white/70 truncate">{l.labels[f.feature] || f.label}</p>
                </div>
                <p className="mt-1 text-2xl font-bold text-white">{(f.total || 0).toLocaleString()}</p>
                <p className="text-[11px] text-white/60">{l.d30}: <strong>{(f.last_30_days || 0).toLocaleString()}</strong> · {l.d365}: <strong>{(f.last_365_days || 0).toLocaleString()}</strong></p>
              </div>
            ))}
          </div>

          {/* Monthly stacked chart */}
          <div>
            <h4 className="text-sm font-bold mb-2">{l.monthly}</h4>
            <div className="space-y-1.5" data-testid="dsia-by-month">
              {(data.by_month || []).map(row => (
                <div key={row.month} className="grid grid-cols-[64px_1fr_60px] items-center gap-2 text-xs">
                  <span className="text-white/60 font-mono">{row.month}</span>
                  <div className="h-4 rounded-full bg-white/5 overflow-hidden flex">
                    {Object.entries(row.features || {}).map(([feat, v]) => (
                      v > 0 ? (
                        <div key={feat} className={`h-full ${COLORS[feat] || 'bg-white/40'}`}
                             style={{ width: `${(v / maxMonth) * 100}%` }} title={`${l.labels[feat]}: ${v}`} />
                      ) : null
                    ))}
                  </div>
                  <span className="text-right font-semibold text-white/80">{row.total.toLocaleString()}</span>
                </div>
              ))}
            </div>
          </div>

          {/* Yearly */}
          <div>
            <h4 className="text-sm font-bold mb-2">{l.yearly}</h4>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="dsia-by-year">
              {(data.by_year || []).map(row => (
                <div key={row.year} className="p-3 rounded-xl bg-white/5 border border-white/10">
                  <p className="text-xs text-white/60">{row.year}</p>
                  <p className="text-xl font-bold">{row.total.toLocaleString()}</p>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </section>
  );
};

export default DsiaAnalyticsPanel;
